import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {
  deleteUnlinkedCv,
  downloadDocument,
  formatDateTime,
  formatFileSize,
  IDocumentModel,
  loadUnlinkedCvs,
} from '../shared/DocumentApi'

export const HR_UNLINKED_CV_PATH = '/hr/unlinked-cv'

/**
 * Hiçbir başvuruya bağlanmamış CV'ler: aday dosyayı yükleyip başvuruyu tamamlamamış.
 * Kişisel veri içerdikleri için gereksiz olanların silinmesi önerilir.
 */
const UnlinkedCvPage: FC = () => {
  const [documents, setDocuments] = useState<IDocumentModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [deletingItem, setDeletingItem] = useState<IDocumentModel | null>(null)

  const fetchDocuments = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadUnlinkedCvs(page)).data.payload
      setDocuments(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Dosyalar yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDocuments(0)
  }, [fetchDocuments])

  const handleDelete = async () => {
    if (!deletingItem) return
    setLoading(true)
    try {
      await deleteUnlinkedCv(deletingItem.id)
      showSuccessToast('CV silindi')
      await fetchDocuments(currentPage)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'CV silinemedi'))
      setLoading(false)
    } finally {
      setDeletingItem(null)
    }
  }

  const columns: TableColumn<IDocumentModel>[] = [
    {
      key: 'fileName',
      label: 'Dosya',
      render: (_, document) => (
        <div className='d-flex align-items-center gap-3'>
          <i className='bi bi-file-earmark-text fs-2 text-gray-500'></i>
          <span className='fw-semibold text-gray-800'>{document.fileName}</span>
        </div>
      ),
    },
    {key: 'size', label: 'Boyut', width: '110px', render: (size: number) => formatFileSize(size)},
    {key: 'createdDate', label: 'Yüklenme Tarihi', width: '170px', render: (value) => formatDateTime(value)},
  ]

  const actions: TableAction<IDocumentModel>[] = [
    {
      label: 'İndir',
      icon: 'bi bi-download',
      className: 'btn-light-primary me-2',
      onClick: (document) => downloadDocument(document.id, document.fileName).catch(() => showErrorToast('İndirilemedi')),
    },
    {label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: setDeletingItem},
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Bağlanmamış CV'ler</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Başvuruya Bağlanmamış CV'ler</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Aday CV'yi yükleyip başvuruyu tamamlamamış. Kişisel veri içerdiği için gereksiz olanları silebilirsiniz.
            </span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <DataTable
            data={documents}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchDocuments(page)}
            loading={loading}
            emptyMessage='Bağlanmamış CV yok'
          />
        </div>
      </div>

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.fileName ?? ''}" kalıcı olarak silinecek. Devam edilsin mi?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {UnlinkedCvPage}
